import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  startTransition,
  type ReactNode,
} from "react";

import type { TimeWindow } from "@/types";
import { DateDisplayContext } from "@nexus/ui";
import type { Theme } from "@/hooks/useTheme";
import type { AppLayoutMode } from "@/hooks/useAppLayout";
import { normalizeInterfaceDensity, type InterfaceDensity } from "@/lib/interfaceDensity";
import type { NavSidebarMode } from "@/hooks/useNavSidebarState";
import type { TimezoneMode } from "@/lib/dateFormat";
import type { AnalyticsTimeRangeLabel } from "@/lib/dashboard/timeRange";
import { useOptionalWorkspaceAudience } from "@/contexts/WorkspaceAudienceContext";
import { useOptionalAccountSettings } from "@/contexts/AccountSettingsContext";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import {
  readAccountPreferences,
  updateAccountPreferences,
  type AccountPreferencesUpdate,
} from "@/lib/accountPreferences";
import {
  DEFAULT_ANALYTICS_VISUAL_PREFERENCES,
  normalizeAnalyticsChartHeight,
  normalizeDetectionRepresentation,
  type AnalyticsChartHeight,
  type DetectionRepresentation,
} from "@/lib/live/visualPreferences";

const AUTH_KEY = "nexus.ui.auth";
const TENANT_IDS_KEY = "nexus.ui.selectedTenantIds";
const EVENT_IDS_KEY = "nexus.ui.selectedEventIds";
const EVENT_WORKSPACE_SELECTION_KEY = "nexus.ui.eventWorkspaceSelection";
const RECENTS_KEY = "nexus.ui.recents";
const RECENTS_MAX = 5;
const USER_PREFERENCES_KEY = "nexus.ui.preferences";

interface UserPreferences {
  theme: Theme;
  layoutMode: AppLayoutMode;
  density: InterfaceDensity;
  navSidebarMode: NavSidebarMode;
  timeWindow: TimeWindow;
  timezone: TimezoneMode;
  dashboardWidgetIds: Record<string, string[]>;
  analyticsTimeRange: AnalyticsTimeRangeLabel;
  detectionRepresentation: DetectionRepresentation;
  analyticsChartHeight: AnalyticsChartHeight;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: "light",
  layoutMode: "workspace",
  density: "normal",
  navSidebarMode: "expanded",
  timeWindow: "2h",
  timezone: "utc",
  dashboardWidgetIds: {},
  analyticsTimeRange: "Last 6h",
  ...DEFAULT_ANALYTICS_VISUAL_PREFERENCES,
};

function readAuth(): boolean {
  try {
    return localStorage.getItem(AUTH_KEY) === "1";
  } catch {
    return false;
  }
}

function readStringArray(key: string, fallback: string[]): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : fallback;
  } catch {
    return fallback;
  }
}

function writeStringArray(key: string, value: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export interface EventWorkspaceSelection {
  eventId: string | null;
  eventLabel: string | null;
  eventStart: string | null;
  eventEnd: string | null;
  eventTypeLabel: string | null;
  collectionGroup: string | null;
}

const EMPTY_EVENT_WORKSPACE_SELECTION: EventWorkspaceSelection = {
  eventId: null,
  eventLabel: null,
  eventStart: null,
  eventEnd: null,
  eventTypeLabel: null,
  collectionGroup: null,
};

function readEventWorkspaceSelection(): EventWorkspaceSelection {
  try {
    const raw = sessionStorage.getItem(EVENT_WORKSPACE_SELECTION_KEY);
    if (!raw) return EMPTY_EVENT_WORKSPACE_SELECTION;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY_EVENT_WORKSPACE_SELECTION;

    const selection = parsed as Record<string, unknown>;
    return {
      eventId: typeof selection.eventId === "string" ? selection.eventId : null,
      eventLabel: typeof selection.eventLabel === "string" ? selection.eventLabel : null,
      eventStart: typeof selection.eventStart === "string" ? selection.eventStart : null,
      eventEnd: typeof selection.eventEnd === "string" ? selection.eventEnd : null,
      eventTypeLabel:
        typeof selection.eventTypeLabel === "string" ? selection.eventTypeLabel : null,
      collectionGroup:
        typeof selection.collectionGroup === "string" ? selection.collectionGroup : null,
    };
  } catch {
    return EMPTY_EVENT_WORKSPACE_SELECTION;
  }
}

function writeEventWorkspaceSelection(value: EventWorkspaceSelection) {
  try {
    sessionStorage.setItem(EVENT_WORKSPACE_SELECTION_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

/** A recently-visited resource, generic across sections (events, dashboards, …). */
export interface RecentResource {
  kind: string;
  id: string;
  label: string;
  meta?: string;
  href: string;
  visitedAt: number;
}

export function normalizeRecents(recents: RecentResource[]): RecentResource[] {
  const perKindCount = new Map<string, number>();
  const seen = new Set<string>();

  return recents
    .map((recent) => {
      const isEvent =
        recent.kind === "event" ||
        recent.kind === "event-monitor" ||
        recent.kind === "event-trends";
      if (!isEvent) return recent;

      const href = recent.href.replace(/\/trends(?=$|[?#])/, "/overview");
      return {
        ...recent,
        kind: "event",
        href,
        meta: href.includes("/overview") ? "Event Overview" : "Event Monitor",
      };
    })
    .filter((recent) => {
      const key = `${recent.kind}:${recent.id}`;
      if (seen.has(key)) return false;
      seen.add(key);

      const count = (perKindCount.get(recent.kind) ?? 0) + 1;
      perKindCount.set(recent.kind, count);
      return count <= RECENTS_MAX;
    });
}

function readRecents(): RecentResource[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const validRecents = parsed.filter(
      (r): r is RecentResource =>
        r != null &&
        typeof r.kind === "string" &&
        typeof r.id === "string" &&
        typeof r.label === "string" &&
        (r.meta === undefined || typeof r.meta === "string") &&
        typeof r.href === "string" &&
        typeof r.visitedAt === "number",
    );

    return normalizeRecents(validRecents);
  } catch {
    return [];
  }
}

function writeRecents(value: RecentResource[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

function isNavSidebarMode(value: unknown): value is NavSidebarMode {
  return value === "expanded" || value === "collapsed";
}

function normalizeLayoutMode(value: unknown): AppLayoutMode {
  if (value === "compact" || value === "workspace") {
    return value;
  }

  return DEFAULT_PREFERENCES.layoutMode;
}

function isTimeWindow(value: unknown): value is TimeWindow {
  return value === "2h" || value === "4h" || value === "8h" || value === "24h" || value === "48h";
}

function isTimezoneMode(value: unknown): value is TimezoneMode {
  return value === "utc" || value === "local";
}

function isDashboardWidgetIds(value: unknown): value is Record<string, string[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return Object.entries(value).every(
    ([dashboardId, widgetIds]) =>
      typeof dashboardId === "string" &&
      Array.isArray(widgetIds) &&
      widgetIds.every((widgetId) => typeof widgetId === "string"),
  );
}

function isAnalyticsTimeRangeLabel(value: unknown): value is AnalyticsTimeRangeLabel {
  return value === "Last 1h" || value === "Last 6h" || value === "Last 24h" || value === "Last 3d";
}

function readUserPreferences(): UserPreferences {
  try {
    const raw = localStorage.getItem(USER_PREFERENCES_KEY);
    if (!raw) return DEFAULT_PREFERENCES;

    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return DEFAULT_PREFERENCES;
    }

    const data = parsed as Partial<UserPreferences>;

    return {
      theme: isTheme(data.theme) ? data.theme : DEFAULT_PREFERENCES.theme,
      layoutMode: normalizeLayoutMode(data.layoutMode),
      density: normalizeInterfaceDensity(data.density),
      navSidebarMode: isNavSidebarMode(data.navSidebarMode)
        ? data.navSidebarMode
        : DEFAULT_PREFERENCES.navSidebarMode,
      timeWindow: isTimeWindow(data.timeWindow) ? data.timeWindow : DEFAULT_PREFERENCES.timeWindow,
      timezone: isTimezoneMode(data.timezone) ? data.timezone : DEFAULT_PREFERENCES.timezone,
      dashboardWidgetIds: isDashboardWidgetIds(data.dashboardWidgetIds)
        ? data.dashboardWidgetIds
        : DEFAULT_PREFERENCES.dashboardWidgetIds,
      analyticsTimeRange: isAnalyticsTimeRangeLabel(data.analyticsTimeRange)
        ? data.analyticsTimeRange
        : DEFAULT_PREFERENCES.analyticsTimeRange,
      detectionRepresentation: normalizeDetectionRepresentation(data.detectionRepresentation),
      analyticsChartHeight: normalizeAnalyticsChartHeight(data.analyticsChartHeight),
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writeUserPreferences(preferences: UserPreferences) {
  try {
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    /* ignore */
  }
}

interface AppContextValue {
  isLoggedIn: boolean;
  login: () => void;
  logout: () => void;

  selectedTenantIds: string[];
  setSelectedTenantIds: (ids: string[]) => void;

  selectedEventIds: string[];
  setSelectedEventIds: (ids: string[]) => void;

  eventWorkspaceSelection: EventWorkspaceSelection;
  setEventWorkspaceSelection: (selection: EventWorkspaceSelection) => void;

  recents: RecentResource[];
  pushRecent: (
    resource: Omit<RecentResource, "visitedAt">,
    options?: { promote?: boolean },
  ) => void;
  pushRecentEvent: (
    event: { event_id: string; event_label: string; collection_group?: string | null },
    options?: { view?: "monitor" | "overview"; promote?: boolean },
  ) => void;

  referenceTime: Date | null;
  setReferenceTime: (date: Date | null) => void;

  clearDashboardScope: () => void;

  theme: Theme;
  setTheme: (theme: Theme) => void;

  layoutMode: AppLayoutMode;
  setLayoutMode: (mode: AppLayoutMode) => void;

  density: InterfaceDensity;
  setDensity: (density: InterfaceDensity) => void;

  dashboardFullscreen: boolean;
  dashboardFullscreenWidgetId: string | null;
  setDashboardFullscreen: (fullscreen: boolean, widgetId?: string | null) => void;
  toggleDashboardFullscreen: () => void;

  navSidebarMode: NavSidebarMode;
  setNavSidebarMode: (mode: NavSidebarMode) => void;
  toggleNavSidebarMode: () => void;

  dashboardWidgetIds: Record<string, string[]>;
  setDashboardWidgetIds: (dashboardId: string, widgetIds: string[]) => void;

  analyticsTimeRange: AnalyticsTimeRangeLabel;
  setAnalyticsTimeRange: (label: AnalyticsTimeRangeLabel) => void;

  detectionRepresentation: DetectionRepresentation;
  setDetectionRepresentation: (representation: DetectionRepresentation) => void;

  analyticsChartHeight: AnalyticsChartHeight;
  setAnalyticsChartHeight: (height: AnalyticsChartHeight) => void;

  timeWindow: TimeWindow;
  setTimeWindow: (w: TimeWindow) => void;

  timezone: TimezoneMode;
  setTimezone: (tz: TimezoneMode) => void;

  autoRefresh: boolean;
  toggleAutoRefresh: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const accountSettings = useOptionalAccountSettings();
  const authorization = useOptionalAuthorization();
  const workspaceAudience = useOptionalWorkspaceAudience()?.audience;
  const pinnedTenantId =
    workspaceAudience?.kind === "client-demo" ? workspaceAudience.tenantId : null;
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(readAuth);

  const [selectedTenantIds, setSelectedTenantIdsState] = useState<string[]>(() =>
    pinnedTenantId ? [pinnedTenantId] : readStringArray(TENANT_IDS_KEY, []),
  );

  const [selectedEventIds, setSelectedEventIdsState] = useState<string[]>(() =>
    readStringArray(EVENT_IDS_KEY, []),
  );

  const [eventWorkspaceSelection, setEventWorkspaceSelectionState] =
    useState<EventWorkspaceSelection>(readEventWorkspaceSelection);

  const [recents, setRecentsState] = useState<RecentResource[]>(readRecents);

  const [referenceTime, setReferenceTimeState] = useState<Date | null>(null);

  const [localPreferences, setPreferencesState] = useState<UserPreferences>(readUserPreferences);
  // A preview reads the previewed account; display preferences stay the administrator's.
  const savedPreferences = accountSettings?.available
    ? readAccountPreferences(accountSettings.settings)
    : null;
  const preferences = savedPreferences
    ? {
        ...localPreferences,
        theme: savedPreferences.theme,
        layoutMode: savedPreferences.layoutMode,
        timezone: savedPreferences.timezone,
        ...savedPreferences.charts,
      }
    : localPreferences;

  const [dashboardFullscreen, setDashboardFullscreenState] = useState(false);
  const [dashboardFullscreenWidgetId, setDashboardFullscreenWidgetIdState] = useState<
    string | null
  >(null);

  const [autoRefresh, setAutoRefresh] = useState(false);

  // Keeps the local fallback (used during previews and before the account loads) current.
  const mirroredPreferences = savedPreferences ? JSON.stringify(preferences) : null;
  useEffect(() => {
    if (!mirroredPreferences) return;
    const next = JSON.parse(mirroredPreferences) as UserPreferences;
    writeUserPreferences(next);
    setPreferencesState(next);
  }, [mirroredPreferences]);

  useEffect(() => {
    document.documentElement.dataset.density = preferences.density;
  }, [preferences.density]);

  const setPreferences = useCallback(
    (updater: (previous: UserPreferences) => UserPreferences) => {
      const next = updater(preferences);
      writeUserPreferences(next);
      setPreferencesState(next);
      if (!accountSettings?.available) return;

      const changes: AccountPreferencesUpdate = {};
      if (next.theme !== preferences.theme) changes.theme = next.theme;
      if (next.layoutMode !== preferences.layoutMode) changes.layoutMode = next.layoutMode;
      if (next.timezone !== preferences.timezone) changes.timezone = next.timezone;
      if (!authorization?.isClientProfile) {
        if (next.detectionRepresentation !== preferences.detectionRepresentation) {
          changes.charts = { detectionRepresentation: next.detectionRepresentation };
        }
        if (next.analyticsChartHeight !== preferences.analyticsChartHeight) {
          changes.charts = { ...changes.charts, analyticsChartHeight: next.analyticsChartHeight };
        }
      }
      if (Object.keys(changes).length) {
        accountSettings.updateSettings((settings) => updateAccountPreferences(settings, changes));
      }
    },
    [accountSettings, authorization?.isClientProfile, preferences],
  );

  const login = useCallback(() => {
    try {
      localStorage.setItem(AUTH_KEY, "1");
    } catch {
      /* ignore */
    }

    setIsLoggedIn(true);
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_KEY);
    } catch {
      /* ignore */
    }

    setIsLoggedIn(false);
  }, []);

  const setSelectedTenantIds = useCallback(
    (ids: string[]) => {
      const nextIds = pinnedTenantId ? [pinnedTenantId] : ids;
      writeStringArray(TENANT_IDS_KEY, nextIds);
      setSelectedTenantIdsState(nextIds);
    },
    [pinnedTenantId],
  );

  useEffect(() => {
    if (!pinnedTenantId) return;

    writeStringArray(TENANT_IDS_KEY, [pinnedTenantId]);
    setSelectedTenantIdsState([pinnedTenantId]);
  }, [pinnedTenantId]);

  const setSelectedEventIds = useCallback((ids: string[]) => {
    writeStringArray(EVENT_IDS_KEY, ids);
    setSelectedEventIdsState(ids);
  }, []);

  const setEventWorkspaceSelection = useCallback((selection: EventWorkspaceSelection) => {
    writeEventWorkspaceSelection(selection);
    setEventWorkspaceSelectionState(selection);
  }, []);

  const pushRecent = useCallback(
    (resource: Omit<RecentResource, "visitedAt">, options?: { promote?: boolean }) => {
      setRecentsState((previous) => {
        const existing = previous.find((r) => r.kind === resource.kind && r.id === resource.id);

        // Revisiting a known item keeps its position unless promotion is asked
        // (e.g. reached from another page), so the list stays stable in use.
        if (existing && options?.promote !== true) {
          if (
            existing.label === resource.label &&
            existing.href === resource.href &&
            existing.meta === resource.meta
          ) {
            return previous;
          }
          const updated = previous.map((r) =>
            r === existing
              ? { ...r, label: resource.label, href: resource.href, meta: resource.meta }
              : r,
          );
          writeRecents(updated);
          return updated;
        }

        const withNew = [
          { ...resource, visitedAt: Date.now() },
          ...previous.filter((r) => !(r.kind === resource.kind && r.id === resource.id)),
        ];
        // Cap each kind independently so unrelated resource types do not evict each other.
        const perKindCount = new Map<string, number>();
        const next = withNew.filter((r) => {
          const count = (perKindCount.get(r.kind) ?? 0) + 1;
          perKindCount.set(r.kind, count);
          return count <= RECENTS_MAX;
        });
        writeRecents(next);
        return next;
      });
    },
    [],
  );

  const pushRecentEvent = useCallback(
    (
      event: { event_id: string; event_label: string; collection_group?: string | null },
      options?: { view?: "monitor" | "overview"; promote?: boolean },
    ) => {
      const view = options?.view ?? "monitor";
      pushRecent(
        {
          kind: "event",
          id: event.event_id,
          label: event.event_label,
          meta: view === "overview" ? "Event Overview" : "Event Monitor",
          href: `/live/events/${encodeURIComponent(event.event_id)}/${view}`,
        },
        { promote: options?.promote },
      );
    },
    [pushRecent],
  );

  const setReferenceTime = useCallback((date: Date | null) => {
    setReferenceTimeState(date);
  }, []);

  const clearDashboardScope = useCallback(() => {
    setSelectedTenantIds([]);
    setSelectedEventIds([]);
    setReferenceTimeState(null);
  }, [setSelectedEventIds, setSelectedTenantIds]);

  const setTheme = useCallback(
    (theme: Theme) => {
      const dark =
        theme === "dark" ||
        (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

      document.documentElement.classList.toggle("dark", dark);

      startTransition(() => {
        setPreferences((previous) => ({ ...previous, theme }));
      });
    },
    [setPreferences],
  );

  const setLayoutMode = useCallback(
    (layoutMode: AppLayoutMode) => {
      setPreferences((previous) => ({
        ...previous,
        layoutMode,
      }));
    },
    [setPreferences],
  );

  const setDensity = useCallback(
    (density: InterfaceDensity) => {
      document.documentElement.dataset.density = density;
      setPreferences((previous) => ({ ...previous, density }));
    },
    [setPreferences],
  );

  const setDashboardFullscreen = useCallback(
    (fullscreen: boolean, widgetId: string | null = null) => {
      setDashboardFullscreenState(fullscreen);
      setDashboardFullscreenWidgetIdState(fullscreen ? widgetId : null);
    },
    [],
  );

  const toggleDashboardFullscreen = useCallback(() => {
    setDashboardFullscreen(!dashboardFullscreen);
  }, [dashboardFullscreen, setDashboardFullscreen]);

  const setNavSidebarMode = useCallback(
    (navSidebarMode: NavSidebarMode) => {
      setPreferences((previous) => ({
        ...previous,
        navSidebarMode,
      }));
    },
    [setPreferences],
  );

  const toggleNavSidebarMode = useCallback(() => {
    setPreferences((previous) => ({
      ...previous,
      navSidebarMode: previous.navSidebarMode === "expanded" ? "collapsed" : "expanded",
    }));
  }, [setPreferences]);

  const setDashboardWidgetIds = useCallback(
    (dashboardId: string, widgetIds: string[]) => {
      setPreferences((previous) => ({
        ...previous,
        dashboardWidgetIds: {
          ...previous.dashboardWidgetIds,
          [dashboardId]: widgetIds,
        },
      }));
    },
    [setPreferences],
  );

  const setAnalyticsTimeRange = useCallback(
    (analyticsTimeRange: AnalyticsTimeRangeLabel) => {
      setPreferences((previous) => ({
        ...previous,
        analyticsTimeRange,
      }));
    },
    [setPreferences],
  );

  const setDetectionRepresentation = useCallback(
    (detectionRepresentation: DetectionRepresentation) => {
      setPreferences((previous) => ({ ...previous, detectionRepresentation }));
    },
    [setPreferences],
  );

  const setAnalyticsChartHeight = useCallback(
    (analyticsChartHeight: AnalyticsChartHeight) => {
      setPreferences((previous) => ({ ...previous, analyticsChartHeight }));
    },
    [setPreferences],
  );

  const setTimeWindow = useCallback(
    (timeWindow: TimeWindow) => {
      setPreferences((previous) => ({
        ...previous,
        timeWindow,
      }));
    },
    [setPreferences],
  );

  const setTimezone = useCallback(
    (timezone: TimezoneMode) => {
      startTransition(() => {
        setPreferences((previous) => ({ ...previous, timezone }));
      });
    },
    [setPreferences],
  );

  const toggleAutoRefresh = useCallback(() => setAutoRefresh((v) => !v), []);

  return (
    <AppContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,

        selectedTenantIds,
        setSelectedTenantIds,

        selectedEventIds,
        setSelectedEventIds,

        eventWorkspaceSelection,
        setEventWorkspaceSelection,

        recents,
        pushRecent,
        pushRecentEvent,

        referenceTime,
        setReferenceTime,
        clearDashboardScope,

        theme: preferences.theme,
        setTheme,

        layoutMode: preferences.layoutMode,
        setLayoutMode,

        density: preferences.density,
        setDensity,

        dashboardFullscreen,
        dashboardFullscreenWidgetId,
        setDashboardFullscreen,
        toggleDashboardFullscreen,

        navSidebarMode: preferences.navSidebarMode,
        setNavSidebarMode,
        toggleNavSidebarMode,

        dashboardWidgetIds: preferences.dashboardWidgetIds,
        setDashboardWidgetIds,

        analyticsTimeRange: preferences.analyticsTimeRange,
        setAnalyticsTimeRange,

        detectionRepresentation: preferences.detectionRepresentation,
        setDetectionRepresentation,

        analyticsChartHeight: preferences.analyticsChartHeight,
        setAnalyticsChartHeight,

        timeWindow: preferences.timeWindow,
        setTimeWindow,

        timezone: preferences.timezone,
        setTimezone,

        autoRefresh,
        toggleAutoRefresh,
      }}
    >
      <DateDisplayContext value={preferences.timezone}>{children}</DateDisplayContext>
    </AppContext.Provider>
  );
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);

  if (!ctx) {
    throw new Error("useAppContext must be used inside AppProvider");
  }

  return ctx;
}

/** Like {@link useAppContext}, for components that may render outside an AppProvider (isolated tests). */
export function useOptionalAppContext(): AppContextValue | null {
  return useContext(AppContext);
}
